import { useState, useRef, useEffect, useLayoutEffect, useCallback, type CSSProperties } from "react";
import { createPortal } from "react-dom";

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  variant?: "default" | "danger";
  icon?: string;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
}

const MENU_WIDTH = 180;
const GAP = 4;

/**
 * A "⋮" trigger that opens a small action menu. Sprint 12b — the popover is
 * rendered through a portal to `document.body` with `position: fixed`, computed
 * from the trigger's `getBoundingClientRect()`. This frees it from any ancestor
 * `overflow: auto` clipping (the table grid scrolls horizontally, which used to
 * cut the menu off below a row). Position is recomputed on scroll/resize and
 * auto-flips above the trigger when there isn't room below.
 */
export function ActionMenu({ items }: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);

  const reposition = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    // Measure the rendered menu when available; otherwise estimate from the
    // item count so the first paint already flips correctly when needed.
    const menuH = menuRef.current?.offsetHeight ?? items.length * 32 + 8;
    const spaceBelow = window.innerHeight - r.bottom;
    const flip = spaceBelow < menuH + GAP && r.top > menuH + GAP;
    const top = flip ? Math.max(GAP, r.top - menuH - GAP) : r.bottom + GAP;
    const right = Math.max(GAP, window.innerWidth - r.right);
    setCoords({ top, right });
  }, [items.length]);

  // Position synchronously before paint so the menu never appears in the wrong
  // spot for a frame.
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  // Recompute on any scroll (capture phase catches nested scroll containers)
  // and on resize, throttled with requestAnimationFrame.
  useEffect(() => {
    if (!open) return;
    let raf = 0;
    const onScrollResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(reposition);
    };
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open, reposition]);

  // Close on outside click (the portaled menu lives outside the trigger's
  // subtree, so check both refs).
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  if (items.length === 0) return null;

  const trigger: CSSProperties = {
    width: 28,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    borderRadius: "var(--border-radius-md)",
    cursor: "pointer",
    color: "var(--color-text-secondary)",
    fontSize: 16,
    padding: 0,
    flexShrink: 0,
  };

  const popover: CSSProperties = {
    position: "fixed",
    top: coords?.top ?? 0,
    right: coords?.right ?? 0,
    minWidth: MENU_WIDTH,
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: "var(--border-radius-md)",
    padding: 4,
    zIndex: 200,
    fontFamily: "var(--font-sans)",
    // Hidden until positioned to avoid a top-left flash on first open.
    visibility: coords ? "visible" : "hidden",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  };

  const itemStyle = (variant?: "default" | "danger"): CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    height: 32,
    padding: "0 12px",
    border: "none",
    background: "transparent",
    fontSize: 13,
    textAlign: "left",
    borderRadius: "var(--border-radius-sm)",
    color: variant === "danger" ? "var(--c-red-600)" : "var(--color-text-primary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  });

  return (
    <>
      <button
        ref={triggerRef}
        style={trigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Actions"
      >
        <i className="ti ti-dots-vertical" aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div ref={menuRef} role="menu" style={popover}>
            {items.map((item) => (
              <button
                key={item.label}
                role="menuitem"
                style={itemStyle(item.variant)}
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
              >
                {item.icon && (
                  <i className={`ti ti-${item.icon}`} style={{ fontSize: 14 }} aria-hidden="true" />
                )}
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
